import { useEffect, useState, type AnchorHTMLAttributes, type ReactNode } from 'react'
import { loadDocumentMetadata } from '../../lib/documentMetadata'
import { useAuthSession } from '../../lib/authSession'
import { documentReaderHref, type DocumentRef, type ReaderContext } from '../../lib/documentReader'

export type { DocumentMetadata } from '../../lib/documentMetadata'

interface Props extends Omit<AnchorHTMLAttributes<HTMLAnchorElement>, 'href' | 'children'> {
  document: DocumentRef
  context?: ReaderContext
  children?: ReactNode
}

/** Native immediate door: fetching a human title never delays the new page. */
export default function DocumentLink({ document: ref, context, children, ...props }: Props) {
  const token = useAuthSession()
  const key = `${ref.document_id}:${token ?? ''}`
  const [loaded, setLoaded] = useState<{ key: string; title: string | null } | null>(null)
  const title = ref.display_title ?? (loaded?.key === key ? loaded.title : null)
  useEffect(() => {
    let current = true
    setLoaded(null)
    if (!ref.display_title)
      loadDocumentMetadata(ref.document_id)
        .then((doc) => {
          if (current) setLoaded({ key, title: doc.display_title ?? null })
        })
        .catch(() => {})
    return () => {
      current = false
    }
  }, [ref.document_id, ref.display_title, key])
  return (
    <a
      {...props}
      href={documentReaderHref(ref.document_id, { ...context, page: context?.page ?? ref.page })}
      target="_blank"
      rel="noopener noreferrer"
    >
      {children ?? title ?? 'Document'}
    </a>
  )
}
